import { forwardBrowserRuntimeErrors } from '@secureblob/sdk/browserRuntimeErrors'

import { getRuntimeErrorApiUrl, isRuntimeErrorForwardingEnabled } from './config'

export interface RuntimeErrorTelemetryForwardingOptions {
    enabled?: boolean
    endpointUrl?: string
    fetch?: typeof fetch
    flushIntervalMs?: number
    dedupeWindowMs?: number
}

function normalizeFlushInterval(flushIntervalMs: number | undefined): number {
    if (!Number.isFinite(flushIntervalMs)) {
        return 30_000
    }

    return Math.max(1_000, Math.trunc(flushIntervalMs!))
}

/**
 * Best-effort webapp-side runtime-error forwarder.
 *
 * Flushes the shared browser runtime-error buffer periodically and when the page
 * is being hidden so the SecureBlob host can record those failures in its normal
 * telemetry pipeline.
 */
export function installRuntimeErrorTelemetryForwarding(
    options: RuntimeErrorTelemetryForwardingOptions = {},
): () => void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return () => {}
    }

    const enabled = options.enabled ?? isRuntimeErrorForwardingEnabled()
    if (!enabled) {
        return () => {}
    }

    const endpointUrl = (options.endpointUrl ?? getRuntimeErrorApiUrl()).trim()
    if (!endpointUrl) {
        return () => {}
    }

    const flushIntervalMs = normalizeFlushInterval(options.flushIntervalMs)
    let inFlight: Promise<void> | null = null

    const flush = (): Promise<void> => {
        if (inFlight) {
            return inFlight
        }

        inFlight = forwardBrowserRuntimeErrors({
            endpointUrl,
            fetch: options.fetch,
            dedupeWindowMs: options.dedupeWindowMs,
        })
            .then(() => { })
            .catch(() => { })
            .finally(() => {
                inFlight = null
            })
        return inFlight
    }

    const handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            void flush()
        }
    }

    const handlePageHide = () => {
        void flush()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handlePageHide)
    const intervalId = window.setInterval(() => {
        void flush()
    }, flushIntervalMs)

    return () => {
        window.clearInterval(intervalId)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        window.removeEventListener('pagehide', handlePageHide)
    }
}
